import { useState, useCallback, useEffect } from 'react';
import { Header } from './components/Header';
import { BottomNav } from './components/BottomNav';
import { LojasTab } from './components/LojasTab';
import { LojasAVisitarTab } from './components/LojasAVisitarTab';
import { NovaLojaForm } from './components/NovaLojaForm';
import { MapaTab } from './components/MapaTab';
import { RotasTab } from './components/RotasTab';
import { RelatorioTab } from './components/RelatorioTab';
import { ConfigTab } from './components/ConfigTab';
import { SegurosTab } from './components/SegurosTab';
import { LoginScreen } from './components/LoginScreen';
import { ToastContainer, showToast } from './components/Toast';
import { DEFAULT_PROFILE } from './hooks/useLocalStorage';
import { ThemeProvider, useTheme } from './hooks/useTheme';
import { useFirestoreSync } from './hooks/useFirestoreSync';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { geocodeAddress } from './utils/geocode';
import { scheduleReminderCheck, requestNotificationPermission, getReminderSettings } from './utils/notifications';
import './App.css';

function AppContent() {
  const { isDark } = useTheme();
  const { isAuthenticated, loading, user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('lojas');
  
  // Start with empty data - Firestore will populate via real-time listener
  const [lojas, setLojas] = useState([]);
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  
  // Firestore real-time sync
  const { 
    syncStatus, 
    lastSyncTime, 
    isOnline, 
    backendAvailable,
    saveToFirestore
  } = useFirestoreSync(setLojas, setProfile);
  
  // Form state
  const [editingLoja, setEditingLoja] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('visitada'); // 'visitada' or 'a_visitar'
  
  // Map state
  const [pinMode, setPinMode] = useState(null);
  const [focusLoja, setFocusLoja] = useState(null);

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then(() => console.log('Service Worker registered'))
        .catch(err => console.error('SW registration failed:', err));
    }
  }, []);

  // Request notification permission and schedule reminders
  useEffect(() => {
    const settings = getReminderSettings();
    if (settings.enabled) {
      // Request permission on first load
      requestNotificationPermission().then(({ granted }) => {
        if (granted) {
          console.log('Notifications enabled');
        }
      });

      // Schedule reminder checks
      const cleanup = scheduleReminderCheck(lojas);
      return cleanup;
    }
  }, [lojas]);

  // Handle edit loja
  const handleEdit = useCallback((loja, type = 'visitada') => {
    setEditingLoja(loja);
    setFormType(type);
    setShowForm(true);
  }, []);

  // Handle edit from A Visitar tab
  const handleEditAVisitar = useCallback((loja) => {
    handleEdit(loja, 'a_visitar');
  }, [handleEdit]);

  // Handle save loja
  const handleSave = useCallback(async (lojaData) => {
    const isNew = !lojas.find(l => l.id === lojaData.id);
    
    // Calculate next store number for new stores
    let numero = lojaData.numero;
    if (isNew && !numero) {
      // Find the highest existing numero and add 1
      const maxNumero = lojas.reduce((max, l) => {
        const n = l.numero || 0;
        return n > max ? n : max;
      }, 0);
      numero = maxNumero + 1;
    }
    
    // Add timestamp, status, and numero
    const lojaWithTimestamp = {
      ...lojaData,
      numero: numero || lojaData.numero,
      status: lojaData.status || formType, // Use existing status or formType for new lojas
      updatedAt: new Date().toISOString()
    };
    
    // Update local state
    let newLojas;
    if (isNew) {
      newLojas = [...lojas, lojaWithTimestamp];
    } else {
      newLojas = lojas.map(l => l.id === lojaWithTimestamp.id ? lojaWithTimestamp : l);
    }
    
    setLojas(newLojas);
    
    // Save to Firestore
    const saveResult = await saveToFirestore(newLojas, profile);

    setShowForm(false);
    setEditingLoja(null);
    
    const statusLabel = lojaWithTimestamp.status === 'a_visitar' ? 'Loja a visitar' : 'Loja';
    
    if (saveResult.success) {
      showToast(isNew ? `${statusLabel} #${numero} criada!` : `${statusLabel} atualizada!`, 'success');
    } else {
      showToast(`Erro ao salvar: ${saveResult.error || 'Tente novamente'}`, 'error');
      return; // Don't continue with geocoding if save failed
    }

    // Auto-geocode for new lojas without coordinates
    if (isNew && !lojaWithTimestamp.lat && lojaWithTimestamp.address && lojaWithTimestamp.city) {
      const result = await geocodeAddress(
        lojaWithTimestamp.address,
        lojaWithTimestamp.city,
        lojaWithTimestamp.state,
        lojaWithTimestamp.zip
      );
      
      if (result.success) {
        const updatedLojas = newLojas.map(l => 
          l.id === lojaWithTimestamp.id 
            ? { ...l, lat: result.lat, lng: result.lng, updatedAt: new Date().toISOString() }
            : l
        );
        setLojas(updatedLojas);
        await saveToFirestore(updatedLojas, profile);
        showToast('GPS atualizado automaticamente!');
      }
    }
  }, [lojas, profile, saveToFirestore, formType]);

  // Handle delete loja
  const handleDelete = useCallback((id) => {
    const newLojas = lojas.filter(l => l.id !== id);
    setLojas(newLojas);
    saveToFirestore(newLojas, profile);
    setShowForm(false);
    setEditingLoja(null);
    showToast('Loja removida!');
  }, [lojas, profile, saveToFirestore]);

  // Handle mark as visited (convert from a_visitar to visitada)
  const handleMarkAsVisited = useCallback(async (id) => {
    try {
      const loja = lojas.find(l => l.id === id);
      if (!loja) {
        console.error('[App] Store not found with id:', id);
        showToast('Store not found', 'error');
        return;
      }
      
      console.log('[App] Marking store as visited:', loja.name, 'ID:', id, 'Current status:', loja.status);
      
      const today = new Date().toISOString().split('T')[0];
      const updatedLoja = { 
        ...loja, 
        status: 'visitada', 
        visit: today,
        visitHistory: [
          { 
            id: `visit_${Date.now()}`,
            date: today,
            note: 'First visit'
          },
          ...(loja.visitHistory || [])
        ],
        updatedAt: new Date().toISOString() 
      };
      
      const newLojas = lojas.map(l => l.id === id ? updatedLoja : l);
      
      // Update local state FIRST
      setLojas(newLojas);
      
      // Show immediate feedback
      showToast(`Saving ${loja.name}...`, 'info');
      
      // Save to Firestore and WAIT for completion
      const result = await saveToFirestore(newLojas, profile);
      
      if (result.success) {
        console.log('[App] Store marked as visited successfully:', loja.name);
        showToast(`${loja.name} marked as visited!`, 'success');
      } else {
        console.error('[App] Failed to save store:', result.error);
        showToast('Error saving. Please try again.', 'error');
        // Revert local state on failure
        setLojas(lojas);
      }
    } catch (error) {
      console.error('[App] Error marking store as visited:', error);
      showToast('Error saving. Please try again.', 'error');
    }
  }, [lojas, profile, saveToFirestore]);

  // Handle geocode
  const handleGeocode = useCallback(async (id) => {
    const loja = lojas.find(l => l.id === id);
    if (!loja) return;

    const result = await geocodeAddress(
      loja.address,
      loja.city,
      loja.state,
      loja.zip
    );

    if (result.success) {
      const newLojas = lojas.map(l => 
        l.id === id 
          ? { ...l, lat: result.lat, lng: result.lng, updatedAt: new Date().toISOString() }
          : l
      );
      setLojas(newLojas);
      await saveToFirestore(newLojas, profile);
      showToast(result.fallback ? 'GPS aproximado encontrado' : 'GPS atualizado!');
    } else {
      showToast('Não foi possível encontrar coordenadas', 'error');
    }
  }, [lojas, profile, saveToFirestore]);

  // Re-geocode all stores using Google Maps API
  const handleRegeocodeAll = useCallback(async () => {
    const lojasWithAddress = lojas.filter(l => l.address && l.city);
    if (lojasWithAddress.length === 0) return;

    let updatedCount = 0;
    const updatedLojas = [...lojas];

    for (const loja of lojasWithAddress) {
      const result = await geocodeAddress(
        loja.address,
        loja.city,
        loja.state,
        loja.zip
      );

      if (result.success) {
        const index = updatedLojas.findIndex(l => l.id === loja.id);
        if (index !== -1) {
          updatedLojas[index] = {
            ...updatedLojas[index],
            lat: result.lat,
            lng: result.lng,
            updatedAt: new Date().toISOString()
          };
          updatedCount++;
        }
      }
      
      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    setLojas(updatedLojas);
    await saveToFirestore(updatedLojas, profile);
    
    showToast(`GPS atualizado para ${updatedCount} de ${lojasWithAddress.length} lojas!`);
  }, [lojas, profile, saveToFirestore]);

  // Handle pin mode
  const handlePinMode = useCallback((loja) => {
    setPinMode(loja);
    setActiveTab('mapa');
  }, []);

  // Handle pin confirm
  const handlePinConfirm = useCallback(async (lat, lng) => {
    if (pinMode) {
      const newLojas = lojas.map(l => 
        l.id === pinMode.id 
          ? { ...l, lat, lng, updatedAt: new Date().toISOString() }
          : l
      );
      setLojas(newLojas);
      await saveToFirestore(newLojas, profile);
      showToast('Localização salva!');
      setPinMode(null);
    }
  }, [pinMode, lojas, profile, saveToFirestore]);

  // Handle view on map
  const handleViewOnMap = useCallback((loja) => {
    setFocusLoja(loja);
    setActiveTab('mapa');
  }, []);

  // Cancel form
  const handleCancelForm = useCallback(() => {
    setShowForm(false);
    setEditingLoja(null);
  }, []);

  // Cancel pin mode
  const handleCancelPin = useCallback(() => {
    setPinMode(null);
  }, []);

  // Clear focus when leaving map
  useEffect(() => {
    if (activeTab !== 'mapa') {
      setFocusLoja(null);
    }
  }, [activeTab]);

  // Render current tab
  const renderTab = () => {
    // Show form when editing or creating (for both lojas and avisitar tabs)
    if (showForm && (activeTab === 'lojas' || activeTab === 'avisitar')) {
      return (
        <NovaLojaForm
          loja={editingLoja}
          onSave={handleSave}
          onDelete={handleDelete}
          onCancel={handleCancelForm}
          formType={formType}
        />
      );
    }

    switch (activeTab) {
      case 'lojas':
        return (
          <LojasTab
            lojas={lojas}
            onEdit={(loja) => handleEdit(loja, 'visitada')}
            onDelete={handleDelete}
            onGeocode={handleGeocode}
            onPinMode={handlePinMode}
            onViewOnMap={handleViewOnMap}
            onRegeocodeAll={handleRegeocodeAll}
          />
        );
      case 'avisitar':
        return (
          <LojasAVisitarTab
            lojas={lojas}
            onEdit={handleEditAVisitar}
            onDelete={handleDelete}
            onGeocode={handleGeocode}
            onPinMode={handlePinMode}
            onViewOnMap={handleViewOnMap}
            onMarkAsVisited={handleMarkAsVisited}
          />
        );
      case 'mapa':
        return (
          <MapaTab
            lojas={lojas}
            pinMode={pinMode}
            onPinConfirm={handlePinConfirm}
            onCancelPin={handleCancelPin}
            focusLoja={focusLoja}
          />
        );
      case 'rotas':
        return <RotasTab lojas={lojas} />;
      case 'seguros':
        return (
          <SegurosTab
            lojas={lojas}
            setLojas={setLojas}
            onSave={(newLojas) => saveToFirestore(newLojas, profile)}
          />
        );
      case 'relatorio':
        return <RelatorioTab lojas={lojas} />;
      case 'config':
        return (
          <ConfigTab
            lojas={lojas}
            setLojas={setLojas}
            profile={profile}
            setProfile={setProfile}
            syncStatus={syncStatus}
            lastSyncTime={lastSyncTime}
            backendAvailable={backendAvailable}
            user={user}
            onLogout={logout}
            saveToFirestore={saveToFirestore}
          />
        );
      default:
        return null;
    }
  };

  // Show loading screen
  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-[#1a3a5c] to-[#0d2137] rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-3xl">🚗</span>
          </div>
          <p className="text-[var(--text-secondary)]">Carregando...</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] theme-transition">
      <Header 
        syncStatus={syncStatus} 
        backendAvailable={backendAvailable} 
      />
      <main className="pt-20 pb-24 px-4">
        {renderTab()}
      </main>
      <BottomNav activeTab={activeTab} onTabChange={setActiveTab} onLogout={logout} />
      <ToastContainer />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
